/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: QuickSearchModal.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Reusable Quick Search Modal component for searching and
    navigating to items across different modules.

 🔧 Notes:
    - Provides a standardized search modal UI
    - Supports initial loading and search loading states
    - Handles empty states and no results states
    - Fully parametrizable for different modules
    - Supports custom item rendering

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { ReactNode } from 'react';
import { Search, X } from 'lucide-react';

export interface QuickSearchModalItem {
  id: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface QuickSearchModalProps<T extends QuickSearchModalItem = QuickSearchModalItem> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: T[];
  isLoadingInitial: boolean;
  isSearching: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onItemSelect: (item: T) => void;
  currentItemId?: string | number | null;
  renderItem: (item: T, isCurrent: boolean) => ReactNode;
  searchPlaceholder: string;
  minCharsMessage?: string;
  emptyMessage?: string;
  noResultsMessage?: string;
  headerIcon?: ReactNode;
  itemIcon?: ReactNode;
  minSearchChars?: number;
  maxWidth?: string;
}

export function QuickSearchModal<T extends QuickSearchModalItem = QuickSearchModalItem>({
  isOpen,
  onClose,
  title,
  items,
  isLoadingInitial,
  isSearching,
  searchValue,
  onSearchChange,
  onItemSelect,
  currentItemId,
  renderItem,
  searchPlaceholder,
  minCharsMessage,
  emptyMessage,
  noResultsMessage,
  headerIcon,
  itemIcon,
  minSearchChars = 2,
  maxWidth = 'max-w-2xl'
}: QuickSearchModalProps<T>) {
  if (!isOpen) return null;

  const hasSearch = searchValue.trim().length > 0;
  const hasMinChars = searchValue.trim().length >= minSearchChars;
  // For client-side filtering (minSearchChars = 0), show items even with 1 char
  const showSearching = isSearching && (minSearchChars === 0 || hasMinChars);
  const showNoResults = hasSearch && (minSearchChars === 0 || hasMinChars) && items.length === 0 && !isSearching;
  const showEmpty = !hasSearch && items.length === 0 && !isLoadingInitial;
  const showItems = items.length > 0 && (minSearchChars === 0 || !hasSearch || hasMinChars);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl ${maxWidth} w-full mx-4 max-h-[80vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            {headerIcon && <div className="text-primary">{headerIcon}</div>}
            <h3 className="text-lg font-semibold text-foreground">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-all ${
              isSearching
                ? 'text-blue-600 dark:text-blue-400 animate-pulse'
                : 'text-muted-foreground'
            }`} />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          {searchValue && searchValue.length === 1 && minCharsMessage && (
            <p className="text-xs text-muted-foreground mt-2">
              {minCharsMessage}
            </p>
          )}
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-4 max-h-96">
          {isLoadingInitial ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-3 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            </div>
          ) : showSearching ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-3 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-sm text-muted-foreground">Searching...</p>
              </div>
            </div>
          ) : showNoResults ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="space-y-2">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p>{noResultsMessage || 'No results found'}</p>
              </div>
            </div>
          ) : showEmpty ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="space-y-2">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p className="text-sm">{emptyMessage || 'Start typing to search'}</p>
              </div>
            </div>
          ) : showItems ? (
            <div className="space-y-2">
              {items.map((item) => {
                const isCurrent = currentItemId !== undefined && currentItemId !== null && 
                  item.id.toString() === currentItemId.toString();
                return (
                  <div key={item.id}>
                    {renderItem(item, isCurrent)}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

